import { requestJson } from './http-json';
import { FACTORY_PURCHASE_AGREEMENT_VERSION } from './factory-purchase-agreement';

export interface FactoryFile {
  path: string;
  content?: string | null;
  content_base64?: string | null;
  size: number;
  editable: boolean;
}
export interface FactorySkill {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  status: string;
  files: FactoryFile[];
  runtime: 'python' | 'node' | 'prompt';
  entrypoint?: string | null;
  entrypoint_candidates?: string[];
  arguments?: string[];
  stdin_mode?: 'text' | 'json' | 'none';
  dependencies?: Record<string, string>;
  package_digest?: string | null;
  dependency_digest?: string | null;
  input_digest?: string | null;
  base_image?: string | null;
  source_package_id?: string | null;
  environment_id?: string | null;
  error?: string | null;
  network_hosts?: string[];
}
export interface FactoryButton {
  id: string;
  name: string;
  action_type: 'prompt' | 'skill';
  prompt: string;
  skill_id?: string | null;
  execution_mode: 'fill' | 'immediate';
  requires_input: boolean;
}
export interface FactorySection { id: string; name: string; buttons: FactoryButton[] }
export interface FactoryWorkbench { enabled: boolean; sections: FactorySection[] }
export interface FactoryPlan { key: string; enabled: boolean; price_fen: number }
export interface FactoryAvatarAsset {
  filename: 'avatar.webp';
  mime_type: 'image/webp';
  content_base64: string;
  size: number;
  width: 512;
  height: 512;
}
export interface FactoryConfig {
  display_name: string;
  description: string;
  starter_questions: string[];
  avatar?: FactoryAvatarAsset | null;
  documents: Record<string, string>;
  skills: FactorySkill[];
  workbench: FactoryWorkbench;
  publishing: {
    is_public: boolean;
    share_enabled: boolean;
    is_paid: boolean;
    promoter_enabled: boolean;
    promoter_commission_bps: number;
    plans: FactoryPlan[];
  };
}
export interface FactoryVersion {
  id: string;
  revision: number;
  created_at: string;
  service_purchase_agreement_version?: string | null;
  service_purchase_agreement_accepted_at?: string | null;
}
export type FactoryAuditStatus = 'draft' | 'not_required' | 'pending' | 'approved' | 'rejected';
export interface FactoryExpert {
  id: string;
  profile_id: string;
  revision: number;
  published_revision: number | null;
  active_version_id: string | null;
  status: string;
  audit_status: FactoryAuditStatus;
  config: FactoryConfig;
  versions: FactoryVersion[];
  share_url: string | null;
  ilink_url?: string | null;
  poster_url?: string | null;
  created_at: string;
  updated_at: string;
}
export interface FactoryExpertSummary {
  id: string;
  profile_id: string;
  revision: number;
  published_revision: number | null;
  active_version_id: string | null;
  status: string;
  audit_status: FactoryAuditStatus;
  config: {
    display_name: string;
    description: string;
    avatar?: FactoryAvatarAsset | null;
    publishing: Pick<FactoryConfig['publishing'], 'is_public' | 'is_paid'>;
  };
  avatar_url?: string | null;
  share_url: string | null;
  created_at: string;
  updated_at: string;
}
export interface FactoryCredential { name: string; host: string; header_name: string; version: number; updated_at: string }
export interface FactorySale {
  id: string;
  order_id?: string;
  plan_key?: string;
  amount_fen?: number;
  price_fen?: number;
  status?: string;
  payment_status?: string;
  fulfillment_status?: string;
  created_at: string;
}
export interface FactorySuggestions {
  source_revision: number;
  display_name: string;
  description: string;
  starter_questions: string[];
}
export interface FactoryDocumentSuggestions {
  source_revision: number;
  documents: Record<'SOUL.md' | 'STYLE.md' | 'MEMORY.md', string>;
}
export interface FactoryMaterial { name: string; content_base64: string; mime_type: string }
export interface FactoryRun {
  id: string;
  status: string;
  message?: string;
  result?: { text?: string; stderr?: string; files?: Array<{ name: string; url: string; mime_type?: string }> } | null;
  error?: string | null;
  revision?: number;
  stderr?: string | null;
}
export interface FactoryMiniappEntry {
  entry_code: string;
  profile_id: string;
  status: string;
  env_version: 'develop' | 'trial' | 'release';
  ordinary_qr_url: string | null;
  has_miniapp_code: boolean;
  updated_at: string;
}

const BASE = '/api/v1/me/factory/experts';
const path = (id: string, suffix = '') => `${BASE}/${encodeURIComponent(id)}${suffix}`;
const json = (method: string, body?: unknown): RequestInit => ({
  method, ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
});

export const listFactoryExperts = () => requestJson<{ items: FactoryExpertSummary[] }>(BASE, json('GET'), '加载我的专家');
export const createFactoryExpert = (display_name?: string) => requestJson<FactoryExpert>(BASE, json('POST', { display_name }), '创建专家');
export const getFactoryExpert = (id: string) => requestJson<FactoryExpert>(path(id), json('GET'), '加载专家');
export const saveFactoryExpert = (id: string, expected_revision: number, config: FactoryConfig) => requestJson<FactoryExpert>(path(id), json('PATCH', { expected_revision, config }), '保存草稿');
export function publishFactoryExpert(
  id: string,
  expected_revision: number,
  agreementAccepted: boolean,
) {
  if (!agreementAccepted) {
    throw new Error('发布前须阅读并同意智能体技术服务采购协议');
  }
  return requestJson<FactoryExpert>(
    path(id, '/publish'),
    json('POST', {
      expected_revision,
      service_purchase_agreement_version: FACTORY_PURCHASE_AGREEMENT_VERSION,
      service_purchase_agreement_accepted: true,
    }),
    '发布专家',
  );
}
export const stopFactoryExpert = (id: string, expected_revision: number) => requestJson<FactoryExpert>(path(id, '/stop'), json('POST', { expected_revision }), '停止新展示与销售');
export const revokeFactoryShare = (id: string, expected_revision: number) => requestJson<FactoryExpert>(path(id, '/share/revoke'), json('POST', { expected_revision }), '撤销分享');
export const rotateFactoryShare = (id: string, expected_revision: number) => requestJson<FactoryExpert>(path(id, '/share/rotate'), json('POST', { expected_revision }), '重新生成分享链接');
export const getFactoryMiniappEntry = (id: string) => requestJson<{ entry: FactoryMiniappEntry | null }>(path(id, '/miniapp-entry'), json('GET'), '加载小程序入口');
export const createFactoryMiniappEntry = (id: string) => requestJson<FactoryMiniappEntry>(path(id, '/miniapp-entry'), json('POST'), '生成小程序码');
export const rotateFactoryMiniappEntry = (id: string) => requestJson<FactoryMiniappEntry>(path(id, '/miniapp-entry/rotate'), json('POST'), '重新生成小程序码');
export const revokeFactoryMiniappEntry = (id: string) => requestJson<{ revoked: boolean }>(path(id, '/miniapp-entry'), json('DELETE'), '撤销小程序入口');
export const factoryMiniappCodeUrl = (id: string) => path(id, '/miniapp-code.png');
export const factoryOrdinaryQrUrl = (id: string) => path(id, '/ordinary-qr.png');
export const deleteFactoryExpert = (id: string, expected_revision: number) => requestJson<unknown>(path(id, `?expected_revision=${expected_revision}`), json('DELETE'), '删除专家');
export const restoreFactoryVersion = (id: string, versionId: string, expected_revision: number) => requestJson<FactoryExpert>(path(id, `/versions/${encodeURIComponent(versionId)}/restore`), json('POST', { expected_revision }), '恢复版本到草稿');
export function uploadFactorySkill(id: string, expected_revision: number, file: File, replace_skill_id?: string) {
  const body = new FormData();
  body.append('file', file);
  body.append('expected_revision', String(expected_revision));
  if (replace_skill_id) body.append('replace_skill_id', replace_skill_id);
  return requestJson<FactoryExpert>(path(id, '/skills'), { method: 'POST', body }, '上传技能');
}
export function uploadFactoryAvatar(id: string, expected_revision: number, file: File) {
  const body = new FormData();
  body.append('file', file);
  body.append('expected_revision', String(expected_revision));
  return requestJson<FactoryExpert>(path(id, '/avatar'), { method: 'POST', body }, '上传头像');
}
export const deleteFactoryAvatar = (id: string, expected_revision: number) => requestJson<FactoryExpert>(path(id, `/avatar?expected_revision=${expected_revision}`), json('DELETE'), '删除头像');
export const prepareFactorySkill = (id: string, skillId: string, expected_revision: number) => requestJson<FactoryExpert>(path(id, `/skills/${encodeURIComponent(skillId)}/prepare`), json('POST', { expected_revision }), '准备技能环境');
export const deleteFactorySkill = (id: string, skillId: string, expected_revision: number) => requestJson<FactoryExpert>(path(id, `/skills/${encodeURIComponent(skillId)}?expected_revision=${expected_revision}`), json('DELETE'), '删除技能');
export const listFactoryCredentials = (id: string) => requestJson<{ items: FactoryCredential[] }>(path(id, '/credentials'), json('GET'), '加载外部连接');
export const saveFactoryCredential = (id: string, name: string, value: { host: string; header_name: string; secret: string }) => requestJson<FactoryCredential>(path(id, `/credentials/${encodeURIComponent(name)}`), json('PUT', value), '保存外部连接');
export const deleteFactoryCredential = (id: string, name: string) => requestJson<unknown>(path(id, `/credentials/${encodeURIComponent(name)}`), json('DELETE'), '撤销外部连接');
export const listFactorySales = (id: string) => requestJson<{ items: FactorySale[]; total_paid_fen: number }>(path(id, '/sales'), json('GET'), '加载销售记录');
export const suggestFactoryMetadata = (id: string, idempotency_key: string) => requestJson<FactorySuggestions>(path(id, '/metadata-suggestions'), json('POST', { idempotency_key }), '生成基本信息建议');
export const suggestFactoryDocuments = (id: string, idempotency_key: string) => requestJson<FactoryDocumentSuggestions>(path(id, '/document-suggestions'), json('POST', { idempotency_key }), '生成专家文档建议');
export const createFactoryRun = (id: string, value: { message: string; skill_id?: string; idempotency_key: string; materials?: FactoryMaterial[]; history?: Array<{ role: 'user' | 'assistant'; content: string }> }) => requestJson<FactoryRun>(path(id, '/runs'), json('POST', value), '开始试用');
export const getFactoryRun = (id: string, runId: string) => requestJson<FactoryRun>(path(id, `/runs/${encodeURIComponent(runId)}`), json('GET'), '加载试用进度');
export const cancelFactoryRun = (id: string, runId: string) => requestJson<FactoryRun>(path(id, `/runs/${encodeURIComponent(runId)}/cancel`), json('POST'), '取消试用');
